import { useState, type VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';

import { plural } from '../../../../../../../../shared/helpers/plural';
import { useAddToast } from '../../../../../../../contexts/ToastContext';
import useAddMembersToTeamGroup from '../../../../../../../hooks/api/teams/teamGroups/useAddMembersToTeamGroup';
import { getErrorMessage } from '../../../../../../../ReactQuery/errors';
import Button from '../../../../../../generic/Button/Button';
import ButtonGroup from '../../../../../../generic/Button/ButtonGroup';
import LoaderButton from '../../../../../../generic/Button/LoaderButton';
import FormGroup from '../../../../../../generic/Forms/FormGroup';
import FormLabel from '../../../../../../generic/Forms/FormLabel';
import LabeledSelect from '../../../../../../generic/Forms/LabeledSelect';
import useSelectRoles from '../../../hooks/useSelectRoles';
import { AddableMembersSelect } from '../../../shared/AddableMembersSelect';
import type { AddableUser } from '../../../management/AddUserOption';

type Props = {
  isOpen: boolean;
  onHide: () => void;
  teamGroupId: number;
  teamGroupName: string;
};

/**
 * Modal for adding members to a team group.
 *
 * @param {Props} props - The component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} props.teamGroupName - The name of the team group
 * @param {() => void} props.onHide - Callback when the modal is closed
 */
const AddTeamGroupMemberModal: VoidFunctionComponent<Props> = ({
  isOpen,
  onHide,
  teamGroupId,
  teamGroupName,
}) => {
  const addToast = useAddToast();
  const [selectedUsers, setSelectedUsers] = useState<AddableUser[]>([]);

  const {
    roleOptions,
    onChangeRole,
    selectedRole,
    isLoading: isLoadingRoles,
  } = useSelectRoles({ teamId: teamGroupId });

  const { mutateAsync: addMembersToTeamGroup, isLoading: isSubmitting } = useAddMembersToTeamGroup(teamGroupId, {
    action: 'add',
    users: selectedUsers.map(user => ({ id: user.value, roleId: selectedRole?.value })),
  });

  const onAdd = async () => {
    try {
      await addMembersToTeamGroup();
      addToast({
        type: 'success',
        content: `Added ${selectedUsers.length} ${plural(selectedUsers.length, 'user')} to "${teamGroupName}"`,
      });
      onHide();
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, `Failed to add ${plural(selectedUsers.length, 'user')} to "${teamGroupName}"`),
      });
    }
  };

  const isAddButtonDisabled = !selectedUsers.length || !selectedRole || isSubmitting;

  return (
    <Modal
      show={isOpen}
      onHide={onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title className="margin-bottom-sm">
          Add Members to &ldquo;{teamGroupName}&rdquo;
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup>
          <FormLabel label="Select Users">
            <AddableMembersSelect
              id="add-team-group-member-modal-user-select"
              teamId={teamGroupId}
              value={selectedUsers}
              onChange={setSelectedUsers}
              shouldFetch={isOpen}
            />
          </FormLabel>
          <LabeledSelect
            id="add-team-group-member-modal-default-role-select"
            label="Role (for added users)"
            placeholder="Select a role"
            options={roleOptions}
            onChange={onChangeRole}
            isDisabled={isLoadingRoles}
          />
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
          >
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={onAdd}
            isDisabled={isAddButtonDisabled}
            isLoading={isSubmitting}
          >
            Add
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTeamGroupMemberModal;
